import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import camelCase from 'lodash/camelCase';
import isNil from 'lodash/isNil';

import DiscoveryContentPopover from 'modules/freemium/components/discovery-content-popover/DiscoveryContentPopover';

import Link from 'libs/router/Link';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { isActive } from 'utils/navigation';
import { __t, applyTranslations } from 'utils/language';

import type { NavigationNavItem, Organization } from 'types';

import './page-subnavigation.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./page-subnavigation.i18n.json'));

const POPOVER_BY_FEATURE_NAME = {
  discoveryContent: DiscoveryContentPopover
};

type OwnProps = {
  items: Array<NavigationNavItem>
};

type StateProps = {
  currentOrganization: Organization,
  pathname: string
};

type Props = OwnProps & StateProps;

class PageSubnavigation extends React.PureComponent<Props> {
  handleTrackSubnavClick = (event: SyntheticEvent<HTMLElement>): void => {
    const target = event.currentTarget;
    const trackingKey = target.dataset.trackingKey || '';

    addUserAction({
      eventCategory: 'subnavigation',
      eventAction: trackingKey
    });
  };

  renderPopover(item: NavigationNavItem, children: React.ReactNode): React.ReactNode {
    const Popover = item.key ? POPOVER_BY_FEATURE_NAME[camelCase(item.key)] : null;

    if (isNil(Popover)) {
      return null;
    }

    return (
      <Popover
        key={`page-subnavigation-popover--${String(item.key)}`}
        triggerComponent={children}
      />
    );
  }

  renderItem = (item: NavigationNavItem, index: number): React.ReactNode => {
    const { currentOrganization, pathname } = this.props;

    const active = isActive(item, pathname);
    const shouldLinkBeBlocked = currentOrganization.isFreemium && !item.isFreemiumAllowed;

    const linkClasses = classnames({
      'page-subnavigation__link': true,
      'page-subnavigation__link--active': active,
      'page-subnavigation__link--active-is-freemium': shouldLinkBeBlocked
    });

    return (
      <li
        key={`${String(active)}-${index}`}
        className="page-subnavigation__item"
      >
        <Link
          to={shouldLinkBeBlocked ? '#' : item.url}
          className={linkClasses}
          onClick={this.handleTrackSubnavClick}
          data={{
            'tracking-key': item.trackingKey,
            'url': item.url
          }}
        >
          {item.glossaryKey}
          {item.isBeta && (
            <React.Fragment>
              &nbsp;–&nbsp;
              <span className="page-subnavigation__link-extension">
                {__t('page_subnavigation.beta_label')}
              </span>
            </React.Fragment>
          )}
        </Link>
      </li>
    );
  };

  render(): React.ReactNode {
    const { items, currentOrganization } = this.props;

    if (!items || !items.length || !currentOrganization) {
      return null;
    }

    return (
      <section className="page-subnavigation">
        <div className="layout-container">
          <ul className="page-subnavigation__items">
            {
              items.map((item, index) => (
                currentOrganization.isFreemium && !item.isFreemiumAllowed
                  ? this.renderPopover(item, this.renderItem(item, index))
                  : this.renderItem(item, index)
              ))
            }
          </ul>
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ organizations, router }): StateProps => ({
  pathname: router.location.pathname,
  currentOrganization: organizations.currentOrganization
});

export default connect(mapStateToProps)(PageSubnavigation);
